// frontend/src/screens/FilteredRouletteRoom.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EmojiPicker from 'emoji-picker-react';
import { SIGN_EMOJI, genderEmoji, toAbsolutePhoto } from '../utils/rouletteUi';
import { REST_API_ORIGIN } from '../platform/endpoints';
import { getAuthToken } from '../utils/auth';
import {
  socket,
  connectAuthenticatedSocket,
  // comunes
  getRoomUsers as getRoomUsersEmit,
  onRoomUsers as onRoomUsersEvt,
  onMessage as onMessageEvt,
  onLikesUpdate as onLikesUpdateEvt,
  toggleLike as toggleLikeEmit,
  sendRouletteMessage,
  // ruleta normal
  joinRoulette,
  onRoulettePartner,
  onWaitingPartner,
  onRouletteMatchEnded,
  // ruleta opuestos
  joinOppositesRoulette,
  onOppositesRoulettePartner,
  onWaitingOppositesPartner,
  // ruleta compatibles
  joinCompatiblesRoulette,
  onCompatiblesRoulettePartner,
  onWaitingCompatiblesPartner,
  cancelRoulette,
} from '../services/socket';
import {
  isCompatiblePair,
  isOppositePair,
  toCanonical,
} from '../utils/zodiacCompatibility';

const API_ORIGIN = REST_API_ORIGIN;

const ZODIAC_TRANSLATION_KEYS = {
  aries: 'aries',
  tauro: 'taurus',
  geminis: 'gemini',
  cancer: 'cancer',
  leo: 'leo',
  virgo: 'virgo',
  libra: 'libra',
  escorpio: 'scorpio',
  sagitario: 'sagittarius',
  capricornio: 'capricorn',
  acuario: 'aquarius',
  piscis: 'pisces',
};

export default function FilteredRouletteRoom() {
  const { t, i18n } = useTranslation(['home', 'common', 'rooms']);
  const { mode } = useParams(); // 'compatibles' | 'opuestos'
  const navigate = useNavigate();

  const [partner, setPartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [waiting, setWaiting] = useState(true);
  const [room, setRoom] = useState(null);
  const [usersRaw, setUsersRaw] = useState([]);
  const [likesState, setLikesState] = useState({}); // messageId -> likesCount
  const [messageError, setMessageError] = useState('');

  // Perfil local (debe ir antes de defaultSeeking para evitar TDZ)
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const mySign = toCanonical(storedUser.zodiacSign || '');
  const myPhoto = toAbsolutePhoto(storedUser.photoPath || '', API_ORIGIN);
  const myName = storedUser.fullName || 'Anónimo';
  const myGender = storedUser.gender || null;
  const myKey = storedUser.userId || storedUser._id || storedUser.id || myName; // clave de like

  // === Preferencia de género buscado (persistida)
  const defaultSeeking = (() => {
    try {
      const saved = localStorage.getItem('seekingGender');
      if (saved) return saved;
    } catch {}
    const g = (storedUser.gender || '').toLowerCase();
    if (g === 'mujer') return 'hombre';
    if (g === 'hombre') return 'mujer';
    return 'todos';
  })();
  const [seekingGender, setSeekingGender] = useState(defaultSeeking);

  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const [draft, setDraft] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const leftRef = useRef(false);
  const seekingGenderRef = useRef(seekingGender);
  const partnerRef = useRef(null);
  const roomRef = useRef(null);
  const joinNowRef = useRef(() => {});
  const nextJoinTimerRef = useRef(null);
  const didMountSeekingRef = useRef(false);

  useEffect(() => {
    try { localStorage.setItem('seekingGender', seekingGender); } catch {}
    seekingGenderRef.current = seekingGender;
  }, [seekingGender]);

  useEffect(() => {
    if (!didMountSeekingRef.current) {
      didMountSeekingRef.current = true;
      return;
    }
    if (leftRef.current) return;
    leftRef.current = false;

    try {
      if (nextJoinTimerRef.current) clearTimeout(nextJoinTimerRef.current);
    } catch {}
    nextJoinTimerRef.current = null;

    try { cancelRoulette(); } catch {}
    setWaiting(true);
    setPartner(null);
    setMessages([]);
    setUsersRaw([]);
    setLikesState({});
    setRoom(null);

    nextJoinTimerRef.current = setTimeout(() => {
      if (leftRef.current) return;
      if (joinNowRef.current) joinNowRef.current(seekingGender);
      nextJoinTimerRef.current = null;
    }, 60);
  }, [seekingGender]);

  // fingerprints locales para reconocer eco del server
  const pendingSendsRef = useRef([]); // [{ text, at }]

  const predicate = (a, b) => (mode === 'opuestos' ? isOppositePair(a, b) : isCompatiblePair(a, b));

  // Join acorde al modo actual (reutilizable)
  const doJoinNow = useCallback((overrideSeekingGender) => {
    if (leftRef.current) return;

    const profile = {
      fullName: myName,
      zodiacSign: storedUser.zodiacSign,
      photoPath: storedUser.photoPath,
      gender: myGender,
      seekingGender: overrideSeekingGender ?? seekingGenderRef.current,
    };

    if (mode === 'opuestos') {
      joinOppositesRoulette(profile);
    } else if (mode === 'compatibles') {
      joinCompatiblesRoulette(profile);
    } else {
      joinRoulette(profile);
    }
  }, [mode, myName, myGender, storedUser.zodiacSign, storedUser.photoPath]);

  useEffect(() => {
  joinNowRef.current = doJoinNow;
  }, [doJoinNow]);
  
useEffect(() => {
  partnerRef.current = partner;
}, [partner]);
  useEffect(() => {
    roomRef.current = room;
  }, [room]);
  
  const handleNext = () => {
    leftRef.current = false;
    try {
      if (nextJoinTimerRef.current) clearTimeout(nextJoinTimerRef.current);
    } catch {}
    nextJoinTimerRef.current = null;

    try { cancelRoulette(); } catch {}
    setWaiting(true);
    setPartner(null);
    setMessages([]);
    setUsersRaw([]);
    setLikesState({});
    setRoom(null);

    nextJoinTimerRef.current = setTimeout(() => {
      if (leftRef.current) return;
      doJoinNow();
      nextJoinTimerRef.current = null;
    }, 60);
  };

  // Preload de foto del partner para evitar parpadeo en móvil
  useEffect(() => {
    if (!partner?.photoPath) return;
    const img = new Image();
    img.src = partner.photoPath;
  }, [partner?.photoPath]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token || !mySign) {
      navigate('/login');
      return;
    }
    leftRef.current = false;

    const unsubscribeMatchEnded = onRouletteMatchEnded((data) => {
      const endedRoom = String(data?.room || data?.matchId || '');
      if (!roomRef.current || endedRoom !== String(roomRef.current)) return;
      partnerRef.current = null;
      roomRef.current = null;
      setWaiting(true);
      setPartner(null);
      setMessages([]);
      setUsersRaw([]);
      setLikesState({});
      setRoom(null);
      setMessageError('');
    });

    const safeCancel = () => {
      if (leftRef.current) return;
      leftRef.current = true;
      try {
        if (nextJoinTimerRef.current) clearTimeout(nextJoinTimerRef.current);
      } catch {}
      nextJoinTimerRef.current = null;
      try { cancelRoulette(); } catch {}
    };

    const handlePartner = (data) => {
      const otherSign = toCanonical(data.zodiacSign || '');
      if (!mySign || !otherSign || !predicate(mySign, otherSign)) {
        // si no cumple el predicado del modo, reintentar
        doJoinNow();
        setWaiting(true);
        setPartner(null);
        setMessages([]);
        setUsersRaw([]);
        setRoom(null);
        return;
      }
      // Filtro local por preferencia de género (fallback si el servidor aún no filtra)
      const desiredSeeking = seekingGenderRef.current || 'todos';
      const partnerGenderKey = String(data.gender || data.snapshotUser?.gender || '').toLowerCase();
      if (desiredSeeking !== 'todos' && partnerGenderKey && partnerGenderKey !== desiredSeeking) {
        doJoinNow(desiredSeeking);
        setWaiting(true);
        setPartner(null);
        setMessages([]);
        setUsersRaw([]);
        setRoom(null);
        return;
      }
      const pPath = data.photoPath && !/^https?:\/\//i.test(data.photoPath)
        ? toAbsolutePhoto(data.photoPath, API_ORIGIN)
        : (data.photoPath || '/default-profile.png');

      partnerRef.current = { ...data, photoPath: pPath };
      roomRef.current = data.room;
      setPartner(partnerRef.current);
      setRoom(data.room);
      setWaiting(false);
      setMessages([]);
      setUsersRaw([]);
      setLikesState({});
      getRoomUsersEmit(data.room);
    };

    const handleWaiting = () => {
      partnerRef.current = null;
      roomRef.current = null;
      setWaiting(true);
      setPartner(null);
      setMessages([]);
      setUsersRaw([]);
      setLikesState({});
      setRoom(null);
    };

    const handleMessage = (msg) => {
      const photo = toAbsolutePhoto(msg?.snapshotUser?.photo || '', API_ORIGIN);
      const receivedFullName = msg?.snapshotUser?.fullName || msg?.user || '';
      const snapFullName = receivedFullName || 'Anónimo';
      const snapGender = msg?.snapshotUser?.gender ?? null;
      const snapSign = msg?.snapshotUser?.zodiacSign || '';

      // heurística para determinar si el mensaje es mío
      const myUid = storedUser.userId || storedUser._id || storedUser.id || null;
      const msgUid = msg.userId || msg?.snapshotUser?._id || msg?.snapshotUser?.userId || null;
      const sid = socket?.id || null;
      const msgSid = msg.socketId || msg.fromSocketId || msg?.snapshotUser?.socketId || null;
      const myNameLC = (myName || '').trim().toLowerCase();
      const msgNameLC = (snapFullName || '').trim().toLowerCase();
      const partnerNameLC = (partnerRef.current?.fullName || '').trim().toLowerCase();
      const myPhotoAbs = myPhoto;
      const msgPhotoAbs = photo;

      let mine = false;
      if (myUid && msgUid && String(myUid) === String(msgUid)) {
        mine = true;
      } else if (sid && msgSid && String(sid) === String(msgSid)) {
        mine = true;
      } else {
        const now = Date.now();
        const idx = pendingSendsRef.current.findIndex(
          fp => fp.text === (msg.text || '') && (now - fp.at) <= 15000
        );
        if (idx !== -1) {
          mine = true;
          pendingSendsRef.current.splice(idx, 1);
        } else if (msgNameLC && myNameLC && msgNameLC === myNameLC) {
          mine = true;
        } else if (partner && msgNameLC === partnerNameLC) {
          mine = false;
        } else if (myPhotoAbs && msgPhotoAbs && myPhotoAbs === msgPhotoAbs) {
          mine = true;
        }
      }

      const normalized = {
        ...msg,
        _anonymous: !receivedFullName,
        snapshotUser: {
          fullName: snapFullName,
          photo,
          gender: snapGender,
          zodiacSign: snapSign,
        },
        _mine: mine,
      };
      setMessages(prev => [...prev, normalized]);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    };

    const handleRoomUsers = (payload) => {
      const arr = Array.isArray(payload?.users) ? payload.users : (Array.isArray(payload) ? payload : []);
      setUsersRaw(arr);
    };

    const handleUserLeft = (data) => {
  const systemMessage = {
    id: `left-${Date.now()}`,
    text: data.message,
    system: true,
    timestamp: new Date().toISOString(),
  };
  setMessages(prev => [...prev, systemMessage]);
  setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

  if (leftRef.current) return;

  doJoinNow();
  setWaiting(true);
  setPartner(null);
  setRoom(null);
  setUsersRaw([]);
  setLikesState({});
};

    const handleLikesUpdate = ({ messageId, likesCount }) => {
      setLikesState(prev => ({ ...prev, [messageId]: likesCount }));
    };

    // listeners por modo
    if (mode === 'opuestos') {
      onOppositesRoulettePartner(handlePartner);
      onWaitingOppositesPartner(handleWaiting);
    } else if (mode === 'compatibles') {
      onCompatiblesRoulettePartner(handlePartner);
      onWaitingCompatiblesPartner(handleWaiting);
    } else {
      onRoulettePartner(handlePartner);
      onWaitingPartner(handleWaiting);
    }
    onMessageEvt(handleMessage);
    onRoomUsersEvt(handleRoomUsers);
    onLikesUpdateEvt(handleLikesUpdate);
    socket.on('userLeft', handleUserLeft);

    // join inicial y rejoin en reconexión
    const handleSocketConnect = () => {
      leftRef.current = false;
      joinNowRef.current();
    };

    socket.on('connect', handleSocketConnect);

    if (socket.connected) {
      handleSocketConnect();
    } else {
      try { connectAuthenticatedSocket('filtered-roulette-mount'); } catch {}
    }

    // cerrar picker al click afuera
    const handleClickOutside = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    // ⌨️ Cerrar el picker con Escape
    const handleKeydown = (e) => {
      if (e.key === 'Escape') setShowEmojiPicker(false);
    };
    document.addEventListener('keydown', handleKeydown);

    // cancelar si se oculta la página
    const handlePageHide = () => safeCancel();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') safeCancel();
    };
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribeMatchEnded();
      safeCancel();
      socket.off('userLeft', handleUserLeft);
      socket.off('connect', handleSocketConnect);

      // listeners específicos
      if (mode === 'opuestos') {
        socket.off('oppositesRoulettePartner', handlePartner);
        socket.off('waitingOppositesPartner', handleWaiting);
      } else if (mode === 'compatibles') {
        socket.off('compatiblesRoulettePartner', handlePartner);
        socket.off('waitingCompatiblesPartner', handleWaiting);
      } else {
        socket.off('roulettePartner', handlePartner);
        socket.off('waitingPartner', handleWaiting);
      }

      // comunes
      socket.off('message', handleMessage);
      socket.off('roomUsers', handleRoomUsers);
      socket.off('likesUpdate', handleLikesUpdate);

      document.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const users = useMemo(() => {
    return (usersRaw || []).map(u => {
      const sign = toCanonical(u.zodiacSign || '') || '';
      return {
        ...u,
        zodiacSign: sign,
        emoji: SIGN_EMOJI[sign] || '',
        genderEmoji: genderEmoji(u.gender),
        photoPath: toAbsolutePhoto(u.photoPath || '', API_ORIGIN),
      };
    });
  }, [usersRaw]);
  void users; // marca como usado para evitar warning ESLint sin afectar el runtime

  const toggleLike = (messageId) => {
    if (!room || !messageId) return;
    toggleLikeEmit({ room, messageId, userKey: String(myKey) });
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!room) return;
    const text = draft.trim();
    if (!text) return;

    try {
      pendingSendsRef.current.push({ text, at: Date.now() });
      pendingSendsRef.current = pendingSendsRef.current.filter(x => Date.now() - x.at < 15000);
    } catch {}

    const result = await sendRouletteMessage({ room, text });
    if (result?.ok) {
      setMessageError('');
      setDraft('');
    } else {
      setMessageError(result?.error || 'message_rejected');
    }
  };

  const addEmoji = (emojiData) => {
    setDraft(prev => prev + (emojiData?.emoji ?? ''));
    setTimeout(() => setShowEmojiPicker(false), 0);
    inputRef.current?.focus();
  };

  const goBack = () => { try { cancelRoulette(); } catch {} navigate(-1); };
  const partnerGenderEmoji = partner?.gender ? genderEmoji(partner.gender) : '';

  // ✅ helpers para abrir el perfil del match en la misma pestaña
  const getPartnerId = React.useCallback(() => {
    if (!partner) return null;
    // Prioridad: id directo
    const directId = partner.userId || partner._id || partner.id || partner.partnerId || partner.uid;
    if (directId) return directId;

    // Fallback: buscar en usersRaw por nombre o foto
    const found = usersRaw.find(
      u => u.fullName === partner.fullName || u.photoPath === partner.photoPath
    );
    return found ? (found.userId || found._id || found.id) : null;
  }, [partner, usersRaw]);

  const openPartnerProfile = React.useCallback(() => {
    const pid = getPartnerId();
    if (!pid) {
      console.warn('[filtered-roulette] partner profile unavailable');
      return;
    }
    // guardamos un snapshot simple por si la ProfilePage quiere usarlo
    try {
      const slim = {
        id: pid,
        _id: pid,
        fullName: partner.fullName,
        zodiacSign: partner.zodiacSign,
        gender: partner.gender ?? null,
        photoPath: partner.photoPath || partner.photo || '',
      };
      // cache temporal utilizada por ProfilePage (fallback)
      const cache = JSON.parse(localStorage.getItem('lastRoomUsers') || '[]')
        .filter(u => String(u.id) !== String(pid));
      cache.unshift(slim);
      localStorage.setItem('lastRoomUsers', JSON.stringify(cache.slice(0, 20)));
    } catch {}
    // navegación en la misma pestaña
    navigate(`/profile/${pid}`, { state: { user: partner } });
  }, [getPartnerId, navigate, partner]);

  // Zodiac sign compatibility helpers
  const allSigns = useMemo(() => Object.keys(SIGN_EMOJI), []);

  const compatibleSigns = useMemo(() => {
    if (!mySign) return [];
    return allSigns.filter((s) => isCompatiblePair(mySign, s));
  }, [mySign, allSigns]);

  const oppositeSigns = useMemo(() => {
    if (!mySign) return [];
    return allSigns.filter((s) => isOppositePair(mySign, s));
  }, [mySign, allSigns]);

  const formatSignsList = (arr) => {
    const list = Array.isArray(arr) ? arr : [];
    if (list.length === 0) return '—';

    return list.map((s, idx) => {
      const name = t(`home:zodiacSigns.${ZODIAC_TRANSLATION_KEYS[s] || s}`);
      return (
        <React.Fragment key={`${s}-${idx}`}>
          {idx > 0 ? <span style={{ opacity: 0.9 }}> • </span> : null}
          <span style={{ whiteSpace: 'nowrap' }}>
            {SIGN_EMOJI[s] || ''}{'\u00A0'}{name}
          </span>
        </React.Fragment>
      );
    });
  };

  return (
    <div id="filteredRoot" style={{ isolation: 'isolate', position: 'relative', minHeight: '100dvh', color: '#fff', overflowX: 'hidden' }}>
      <style>{`
  @keyframes wheel-spin {
    from { transform: translate(-50%, -50%) rotate(0deg); }
    to   { transform: translate(-50%, -50%) rotate(360deg); }
  }

  #filteredRoot::before{
    content:"";
    position:fixed;
    inset:0;
    z-index:-3;
    pointer-events:none;
    background-image:
      radial-gradient(circle at 10% 8%, rgba(255,255,255,.85) 0 1px, transparent 1.8px),
      radial-gradient(circle at 78% 7%, rgba(255,215,0,.75) 0 1px, transparent 1.8px),
      radial-gradient(circle at 94% 20%, rgba(183,44,255,.76) 0 1px, transparent 1.8px),
      radial-gradient(circle at 24% 48%, rgba(255,255,255,.45) 0 1px, transparent 1.8px),
      radial-gradient(circle at 68% 56%, rgba(255,215,0,.50) 0 1px, transparent 1.8px),
      radial-gradient(circle at 44% 84%, rgba(255,255,255,.42) 0 1px, transparent 1.8px);
    opacity:.58;
  }

  @media (max-width: 540px) {
    #filteredRoot .roulette-mode-filter {
      width:100% !important;
      justify-content:center !important;
    }
  }
`}</style>

      {/* Fondo base moderno */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: -2,
          background: 'radial-gradient(circle at 50% -8%, rgba(218,165,32,.16), transparent 22%), radial-gradient(circle at 12% 18%, rgba(183,44,255,.25), transparent 34%), radial-gradient(circle at 84% 64%, rgba(138,43,226,.20), transparent 34%), linear-gradient(180deg, #03000b 0%, #100020 48%, #05000d 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Ruleta de fondo */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          width: 'min(112svmin, 1180px)',
          height: 'min(112svmin, 1180px)',
          transform: 'translate(-50%, -50%)',
          backgroundImage: 'url("/logo/Zodiacus.jpeg")',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: 'drop-shadow(0 0 44px rgba(218,165,32,.22)) saturate(1.12)',
          borderRadius: '50%',
          animation: 'wheel-spin 160s linear infinite',
          transformOrigin: 'center center',
          zIndex: -1,
          opacity: .18,
          pointerEvents: 'none',
        }}
      />

      {/* Contenido */}
      <div style={styles.container}>
      <section style={styles.topBar}>
        <button type="button" onClick={goBack} style={styles.topCircleButton} aria-label={t('common:actions.back')}>
          ←
        </button>

        <div style={styles.topTitleWrap}>
          <div style={styles.kicker}>{t('roulette.kicker')}</div>
          <h1 style={styles.title}>
            {mode === 'compatibles'
              ? t('roulette.modes.compatibles.title')
              : mode === 'opuestos'
                ? t('roulette.modes.opuestos.title')
                : t('roulette.modes.zodiacal.title')}
          </h1>
          <p style={styles.topSubtitle}>
            <span style={styles.onlineDot}>●</span> {t('roulette.screen.liveSubtitle')}
          </p>
        </div>
      </section>

      {/* Preferencia de género */}
      <div className="roulette-mode-filter" style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        background: 'linear-gradient(145deg, rgba(7,0,19,.72), rgba(31,5,68,.50))',
        border: '1px solid rgba(218,165,32,.34)',
        padding: '10px 10px', borderRadius: 18, width: 'fit-content',
        boxShadow: '0 18px 40px rgba(0,0,0,.30), inset 0 1px 0 rgba(255,255,255,.08)',
        margin: '0 auto 12px',
        backdropFilter: 'blur(16px) saturate(1.12)',
        WebkitBackdropFilter: 'blur(16px) saturate(1.12)'
      }}>
        <span style={{ fontWeight: 950, color: '#F3C96B', letterSpacing: '.04em' }}>{t('roulette.room.gender.label')}</span>
        {[
          { key: 'hombre' },
          { key: 'mujer' },
          { key: 'todos' },
        ].map(opt => (
          <button
            key={opt.key}
            type="button"
            aria-pressed={seekingGender === opt.key}
            onClick={() => {
              if (seekingGender === opt.key) return;
              leftRef.current = false;
              setSeekingGender(opt.key);
            }}
            style={{
              border: '1px solid rgba(218,165,32,0.45)',
              background: seekingGender === opt.key
                ? 'linear-gradient(135deg,#8A2BE2,#6F1CCB,#DAA520)'
                : 'linear-gradient(135deg, rgba(255,255,255,.10), rgba(255,255,255,.045))',
              color: seekingGender === opt.key ? '#fff' : '#FFE8A3',
              fontWeight: 800,
              borderRadius: 10,
              padding: '8px 10px',
              cursor: 'pointer',
              fontSize: 14,
              boxShadow: seekingGender === opt.key ? '0 2px 10px rgba(0,0,0,0.25)' : 'none'
            }}
            title={t(`roulette.room.gender.options.${opt.key}.title`)}
          >
            {t(`roulette.room.gender.options.${opt.key}.label`)}
          </button>
        ))}
      </div>

      {waiting ? (
        <div style={{ ...styles.infoBox, marginTop: 18 }}>
          <h3 style={styles.infoTitle}>
            {mode === 'compatibles'
              ? t('roulette.room.filtered.compatibles.waitingTitle')
              : t('roulette.room.filtered.opuestos.waitingTitle')}
          </h3>
          <p style={styles.infoText}>
            {mode === 'compatibles' ? (
              <>
                {t('roulette.room.filtered.compatibles.signsLabel')}{' '}
                <strong>{formatSignsList(compatibleSigns) || '—'}</strong>
              </>
            ) : (
              <>
                {t('roulette.room.filtered.opuestos.signsLabel')}{' '}
                <strong>{formatSignsList(oppositeSigns) || '—'}</strong>
              </>
            )}
          </p>
        </div>
      ) : (
        <>
          {partner && (
            <div style={styles.partnerCardC}>
              <img
                src={partner.photoPath || '/default-profile.png'}
                alt={t('rooms:chat.profile.avatarAlt', { name: partner.fullName || t('roulette.room.anonymous') })}
                style={styles.partnerAvatarC}
                loading="eager"
                decoding="async"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = '/default-profile.png';
                }}
                onClick={openPartnerProfile}
                role="button"
                tabIndex={0}
                title={t('roulette.room.actions.profileAndRequest')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPartnerProfile(); } }}
              />

              <div style={styles.partnerInfoCompact}>
                <div style={styles.partnerNameRowCompact}>
                  <span style={styles.partnerNameC}>{partner.fullName}</span>
                  <span style={styles.partnerSign} title={partner.zodiacSign}>
                    {SIGN_EMOJI[toCanonical(partner.zodiacSign || '')] || ''}
                  </span>
                  {partnerGenderEmoji ? (
                    <span style={styles.partnerGender}>{partnerGenderEmoji}</span>
                  ) : null}
                </div>

                <div style={styles.partnerActionsCompact}>
                  <button
                    type="button"
                    onClick={openPartnerProfile}
                    style={styles.viewProfileBtn}
                  >
                    {t('rooms:chat.profile.view')}
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    style={styles.nextButton}
                  >
                    🔄 {t('roulette.room.actions.next')}
                  </button>
                </div>
              </div>
            </div>
          )}


          {/* Chat */}
          <div style={{ ...styles.chatBox, marginBottom: 80, marginTop: 10 }}>
            {messages.map((msg, i) => {
              if (msg.system) {
                return (
                  <div
                    key={(msg.id ? String(msg.id) : `i-${i}`) + '-' + (msg?.snapshotUser?.userId || msg.userId || msg.socketId || msg.fromSocketId || msg?.snapshotUser?.socketId || '')}
                    style={styles.systemMsg}
                  >
                    {msg.text}
                  </div>
                );
              }

              const displayName = msg?._anonymous
                ? t('roulette.room.anonymous')
                : (msg?.snapshotUser?.fullName || msg?.user || t('roulette.room.anonymous'));
              const photo = toAbsolutePhoto(msg?.snapshotUser?.photo || '', API_ORIGIN);
              const emoji = SIGN_EMOJI[toCanonical(msg?.snapshotUser?.zodiacSign || '')] || '';
              const gEmoji = genderEmoji(msg?.snapshotUser?.gender);
              const likesCount = likesState[msg.id] ?? msg.likesCount ?? 0;

              const isMine =
                msg._mine === true ||
                (((msg?.snapshotUser?.fullName || '').trim().toLowerCase() === (myName || '').trim().toLowerCase()) &&
                  !(partner && ((msg?.snapshotUser?.fullName || '').trim().toLowerCase() === (partner.fullName || '').trim().toLowerCase())));

              return (
                <div
                  key={(msg.id ? String(msg.id) : `i-${i}`) + '-' + (msg?.snapshotUser?.userId || msg.userId || msg.socketId || msg.fromSocketId || msg?.snapshotUser?.socketId || '')}
                  style={{
                    ...(isMine ? styles.messageRowMine : styles.messageRowOther),
                    opacity: 0,
                    animation: 'fadeSlideIn 0.3s forwards',
                    animationDelay: `${Math.max(0, i - Math.max(0, messages.length - 6)) * 80}ms`,
                  }}
                >
                  <img
                    src={photo}
                    alt={t('rooms:chat.profile.avatarAlt', { name: displayName })}
                    style={styles.messagePhoto}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = '/default-profile.png';
                    }}
                  />

                  <div style={{ maxWidth: '70vw', wordBreak: 'break-word' }}>
                    <div style={styles.messageUserName}>
                      <span style={isMine ? styles.nameTagMine : styles.nameTagOther}>
                        <span style={{ ...styles.messageName, ...(isMine ? styles.nameMine : styles.nameOther) }}>
                          {displayName}
                        </span>
                      </span>
                      {emoji ? (
                        <span style={styles.signBadge} title={msg?.snapshotUser?.zodiacSign}>
                          {emoji}
                        </span>
                      ) : null}
                      {gEmoji ? <span style={styles.genderBadge}>{gEmoji}</span> : null}
                    </div>

                    <div style={isMine ? styles.messageBubbleMine : styles.messageBubbleOther}>
                      {msg.text || t('rooms:chat.messages.empty')}
                    </div>

                    <div style={styles.messageFooter}>
                      <span style={styles.messageTime}>
                        {(() => {
                          try {
                            const d = msg.timestamp ? new Date(msg.timestamp) : new Date();
                            const hh = String(d.getHours()).padStart(2, '0');
                            const mm = String(d.getMinutes()).padStart(2, '0');
                            return `${hh}:${mm}`;
                          } catch { return ''; }
                        })()}
                      </span>
                      <div style={styles.likeContainer}>
                        <button
                          onClick={() => toggleLike(msg.id)}
                          style={styles.likeButton}
                          aria-label={t('rooms:chat.likes.toggle')}
                          title={t('rooms:chat.likes.title')}
                        >
                          {likesCount > 0 ? '❤️' : '🤍'}
                        </button>
                        <span style={styles.likeCount}>{likesCount}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={chatEndRef} />
          </div>

          {/* Composer flotante + emojis */}
          <div style={styles.composerWrap}>
            <button
  type="button"
  onClick={() => setShowEmojiPicker(prev => !prev)}
  style={styles.emojiToggleButton}
  aria-label={t('rooms:chat.emoji.toggle')}
  title={t('rooms:chat.emoji.title')}
>
  <span
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      transform: 'translateY(-1px)', // ajuste fino para móvil
      userSelect: 'none',
      pointerEvents: 'none',
    }}
  >
    😀
  </span>
</button>
            {showEmojiPicker && (
              <div ref={emojiPickerRef} style={styles.emojiPickerContainer}>
                <EmojiPicker
                  onEmojiClick={addEmoji}
                  locale={(i18n.resolvedLanguage || i18n.language || 'es').toLowerCase().startsWith('en') ? 'en' : 'es'}
                  lazyLoadEmojis={true}
                  searchDisabled={true}
                  emojiStyle="native"
                  theme="light"
                />
              </div>
            )}

            <form onSubmit={sendMessage} style={styles.form}>
              <input
                ref={inputRef}
                id="msg"
                name="msg"
                type="text"
                placeholder={t('rooms:chat.composer.placeholder')}
                style={styles.input}
                autoComplete="off"
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  if (messageError) setMessageError('');
                }}
              />
              <button type="submit" style={styles.sendButton} aria-label={t('rooms:chat.composer.sendMessage')}>➤</button>
            </form>
          </div>
          {messageError && <p role="alert" style={styles.messageError}>{t('roulette.room.errors.sendFailed', { error: messageError })}</p>}

          <style>{`
            @keyframes fadeSlideIn {
              0% { opacity: 0; transform: translateX(-20px); }
              100% { opacity: 1; transform: translateX(0); }
            }
          `}</style>
        </>
      )}
        </div>
      </div>
  );
}

const styles = {
  container: {
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 14px)',
    paddingBottom: 'calc(96px + env(safe-area-inset-bottom, 0px))',
    backgroundColor: 'transparent',
    color: '#fff',
    minHeight: '100dvh',
    position: 'relative',
    zIndex: 1,
    fontFamily: "'Poppins', sans-serif",
    boxSizing: 'border-box',
  },
  topBar: {
    display: 'grid',
    gridTemplateColumns: '40px minmax(0, 1fr)',
    alignItems: 'center',
    gap: 9,
    marginBottom: 12,
    padding: '10px 10px',
    borderRadius: 24,
    border: '1px solid rgba(218,165,32,.32)',
    background: 'linear-gradient(145deg, rgba(7,0,19,.72), rgba(31,5,68,.50))',
    boxShadow: '0 18px 42px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.08)',
    backdropFilter: 'blur(16px) saturate(1.12)',
    WebkitBackdropFilter: 'blur(16px) saturate(1.12)',
  },
  topCircleButton: {
    width: 38,
    height: 38,
    minWidth: 38,
    borderRadius: '50%',
    border: '1px solid rgba(218,165,32,.44)',
    background: 'radial-gradient(circle at 34% 22%, rgba(255,255,255,.14), transparent 34%), rgba(10,0,26,.66)',
    color: '#FFE8A3',
    fontSize: 21,
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    lineHeight: 1,
    padding: 0,
    boxShadow: '0 12px 28px rgba(0,0,0,.32)',
    boxSizing: 'border-box',
  },
  topTitleWrap: {
    minWidth: 0,
  },
  kicker: {
    color: '#F3C96B',
    fontSize: '.58rem',
    fontWeight: 950,
    letterSpacing: '.18em',
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  topSubtitle: {
    margin: '8px 0 0',
    color: 'rgba(255,255,255,.78)',
    fontSize: 13,
    fontWeight: 850,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  onlineDot: {
    color: '#27e38a',
    textShadow: '0 0 12px rgba(39,227,138,.85)',
    margin: '0 4px',
  },
  title: {
    margin: 0,
    color: '#F3C96B',
    fontFamily: 'Georgia, serif',
    fontSize: 'clamp(1.65rem, 7.2vw, 2.35rem)',
    lineHeight: .96,
    letterSpacing: '.01em',
    textShadow: '0 0 24px rgba(218,165,32,.34)',
    whiteSpace: 'nowrap',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  subtitle: {
    textAlign: 'center',
    color: '#FFE8A3',
    marginTop: 4,
    marginBottom: 8,
    fontStyle: 'italic',
    letterSpacing: 0.3,
    display: 'inline-block',
    alignSelf: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
    textShadow: '0 2px 8px rgba(0,0,0,0.6)',
    padding: '6px 12px',
    borderRadius: 999,
    background: 'linear-gradient(135deg, rgba(0,0,0,0.55), rgba(35,0,70,0.45))',
    border: '1px solid rgba(218,165,32,0.65)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.06)'
  },
  subTitleBar: { display: 'flex', justifyContent: 'center', margin: '14px 0 16px' },
  subTitleText: {
    padding: '4px 12px',
    borderRadius: 999,
    background: 'linear-gradient(135deg, rgba(0,0,0,0.38), rgba(35,0,70,0.28))',
    border: '1px solid rgba(218,165,32,0.45)',
    color: '#DEC98A',
    fontSize: 'clamp(0.72rem, 1.5vw, 0.86rem)',
    fontWeight: 500,
    letterSpacing: 0.25,
    textTransform: 'uppercase',
    textShadow: '0 1px 2px rgba(0,0,0,0.45)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(255,255,255,0.04)',
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
    userSelect: 'none',
    fontFamily: '"Cinzel", "Cormorant Garamond", serif',
  },
  headerBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    margin: '4px auto 10px',
    maxWidth: 680,
  },
  statusPill: {
    background: 'rgba(0,0,0,0.45)',
    border: '1px solid rgba(218,165,32,0.6)',
    color: '#FFE8A3',
    padding: '6px 10px',
    borderRadius: 999,
    fontSize: '0.9rem',
    boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
  },
  nextButton: {
    padding: '8px 12px',
    background: 'linear-gradient(135deg,#8A2BE2 0%, #6F1CCB 48%, #DAA520 100%)',
    color: '#fff',
    border: '1px solid rgba(255,215,0,.45)',
    borderRadius: 999,
    fontWeight: 950,
    cursor: 'pointer',
    boxShadow: '0 10px 22px rgba(0,0,0,.28), 0 0 14px rgba(218,165,32,.16)',
    whiteSpace: 'nowrap',
  },

  usersBox: {
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    padding: '6px 8px',
    borderRadius: 8,
    color: '#fff',
    maxWidth: 320,
    marginBottom: 10,
    border: '1px solid rgba(218,165,32,0.28)',
  },
  usersTitle: {
    fontSize: '0.9rem',
    fontWeight: '700',
    color: '#DAA520',
    marginBottom: 6,
    letterSpacing: 0.2,
  },
  usersList: {
    listStyle: 'none',
    paddingLeft: 0,
    margin: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 8,
  },
  userChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(218,165,32,0.35)',
    padding: '6px 10px',
    borderRadius: 999,
    flexWrap: 'nowrap',
    minWidth: 0,
  },
  userChipAvatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: '2px solid #DAA520',
    objectFit: 'cover',
    flexShrink: 0,
    boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
  },
  userChipName: {
    fontFamily: '"Cinzel", "Cormorant Garamond", serif',
    fontWeight: 700,
    letterSpacing: '0.25px',
    fontSize: 'clamp(0.82rem, 3vw, 0.95rem)',
    lineHeight: 1.15,
    color: '#FFE49A',
    textShadow: '0 1px 2px rgba(0,0,0,0.45), 0 0 6px rgba(255,228,154,0.35)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  userChipSign: {
    fontSize: '1rem',
    flexShrink: 0,
  },
  userChipGender: {
    fontSize: '1rem',
    flexShrink: 0,
  },

  chatBox: {

  display: 'flex',

  flexDirection: 'column',

  border: '1px solid rgba(218,165,32,.38)',

  padding: 12,

  minHeight: 360,

  height: 'calc(100dvh - 250px)',

  overflowY: 'auto',

  marginBottom: 80,

  position: 'relative',

  background: 'linear-gradient(145deg, rgba(7,0,19,.82), rgba(31,5,68,.62))',

  backdropFilter: 'blur(18px) saturate(1.12)',

  WebkitBackdropFilter: 'blur(18px) saturate(1.12)',

  color: '#fff',

  borderRadius: 24,

  boxShadow: '0 24px 54px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.08)',

  WebkitOverflowScrolling: 'touch',

  overscrollBehaviorY: 'contain',

  scrollbarGutter: 'stable both-edges',

},
  systemMsg: {
  textAlign: 'center',
  fontStyle: 'italic',
  color: '#FFE8A3',
  marginBottom: 10,
  background: 'rgba(255,255,255,.08)',
  border: '1px solid rgba(218,165,32,.22)',
  borderRadius: 14,
  padding: '7px 12px',
  maxWidth: '78%',
  alignSelf: 'center',
  fontWeight: 700,
  boxShadow: '0 8px 18px rgba(0,0,0,.18)',
},

  // Mensajes
  messageRowOther: {
    borderRadius: 16,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 14,
    padding: 12,
    maxWidth: '75%',
    alignSelf: 'flex-start',
    boxShadow: '0 12px 24px rgba(0,0,0,.22)',
    wordBreak: 'break-word',
    background: 'linear-gradient(135deg, rgba(255,255,255,.10), rgba(255,255,255,.045))',
    border: '1px solid rgba(218,165,32,.28)',
    flexDirection: 'row',
  },
  messageRowMine: {
    borderRadius: 16,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 14,
    padding: 12,
    maxWidth: '75%',
    alignSelf: 'flex-end',
    boxShadow: '0 12px 24px rgba(0,0,0,.22)',
    wordBreak: 'break-word',
    background: 'linear-gradient(135deg, rgba(138,43,226,.22), rgba(75,0,130,.15))',
    border: '1px solid rgba(218,165,32,.26)',
    flexDirection: 'row-reverse',
  },
  messagePhoto: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #DAA520',
  },
  messageUserName: {
    fontWeight: 600,
    textShadow: '0.5px 0.5px 2px rgba(0,0,0,0.25)',
    marginBottom: 2,
    lineHeight: 1.2,
  },
  messageName: {
    marginRight: 6,
    display: 'inline-block',
    maxWidth: '52vw',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: '"Cinzel", "Cormorant Garamond", serif',
    fontWeight: 900,
    letterSpacing: '0.3px',
    fontSize: 'clamp(0.95rem, 3.6vw, 1.05rem)',
    lineHeight: 1.15,
    textTransform: 'none',
  },
  nameTagMine: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    borderRadius: 999,
    background: 'linear-gradient(135deg, rgba(255,248,217,0.92), rgba(255,243,200,0.88))',
    border: '1px solid rgba(218,165,32,0.45)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    color: '#2a0051',
    marginRight: 6,
  },
  nameTagOther: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    borderRadius: 999,
    background: 'linear-gradient(135deg, rgba(75,0,130,0.85), rgba(58,0,102,0.8))',
    border: '1px solid rgba(75,0,130,0.5)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    color: '#FFF6D6',
    marginRight: 6,
  },
  nameMine: {
    color: '#2a0051',
    textShadow: '0 1px 2px rgba(0,0,0,0.25)',
  },
  nameOther: {
    color: '#FFF6D6',
    textShadow: '0 1px 2px rgba(0,0,0,0.45)',
  },
  userNameInline: {
    marginRight: 6,
    display: 'inline-block',
    maxWidth: '48vw',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: '"Cinzel", "Cormorant Garamond", serif',
    fontWeight: 800,
    letterSpacing: '0.25px',
    fontSize: 'clamp(0.85rem, 3vw, 0.98rem)',
    lineHeight: 1.15,
    color: '#FFE49A',
    textShadow: '0 1px 2px rgba(0,0,0,0.45), 0 0 6px rgba(255,228,154,0.35)',
  },

  messageBubbleOther: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, rgba(255,248,217,0.95) 0%, rgba(255,243,200,0.92) 100%)',
    color: '#1f1147',
    padding: '10px 14px',
    borderRadius: 16,
    borderTopLeftRadius: 6,
    border: '1px solid rgba(218,165,32,0.45)',
    boxShadow: '0 6px 14px rgba(0,0,0,0.12), 0 0 0 1px rgba(218,165,32,0.12) inset',
    maxWidth: '65vw',
    marginTop: 4,
  },
  messageBubbleMine: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, rgba(137,106,255,0.96) 0%, rgba(84,58,183,0.95) 100%)',
    color: '#FFD665',
    padding: '10px 14px',
    borderRadius: 16,
    borderTopRightRadius: 6,
    border: '1px solid rgba(75,0,130,0.35)',
    boxShadow: '0 6px 14px rgba(0,0,0,0.15), 0 0 0 1px rgba(75,0,130,0.15) inset',
    maxWidth: '65vw',
    marginTop: 4,
    textShadow: '0 1px 2px rgba(0,0,0,0.35)',
  },
  messageFooter: { marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  messageTime: { fontSize: '0.75rem', color: 'rgba(255,255,255,.62)', fontStyle: 'italic' },
  likeContainer: { display: 'flex', alignItems: 'center', gap: 6, marginLeft: 12 },
  likeButton: {
    fontSize: '1.4rem',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
  },
  likeCount: {
    fontSize: '0.9rem',
    color: '#FFE8A3',
    fontWeight: '700',
    userSelect: 'none',
  },

  // Badges
  signBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.9rem',
    lineHeight: 1,
    width: 22,
    height: 22,
    marginLeft: 6,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #fff6c2 0%, #ffe89a 100%)',
    border: '1px solid rgba(218,165,32,0.6)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
    color: '#4B0082',
    fontWeight: 700,
    userSelect: 'none',
  },
  genderBadge: {
    marginLeft: 6,
    fontSize: '0.95rem',
    lineHeight: 1,
    userSelect: 'none',
    filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))',
  },

 emojiToggleButton: {
  width: 42,
  height: 42,
  minWidth: 42,
  padding: 0,

  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  fontSize: '22px',
  lineHeight: 0,

  background: 'rgba(255,255,255,.10)',
  border: '1px solid rgba(218,165,32,.40)',
  borderRadius: '50%',
  cursor: 'pointer',
  color: '#FFE8A3',
  pointerEvents: 'auto',
  boxSizing: 'border-box',
  WebkitTapHighlightColor: 'transparent',

  boxShadow: '0 10px 20px rgba(0,0,0,.26)',
},
  composerWrap: {
  position: 'fixed',
  left: 12,
  right: 12,
  bottom: 'calc(env(safe-area-inset-bottom, 0px) + 10px)',
  padding: '9px 10px',
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  zIndex: 900,
  pointerEvents: 'none',
  borderRadius: 22,
  background: 'linear-gradient(145deg, rgba(7,0,19,.94), rgba(31,5,68,.88))',
  border: '1px solid rgba(218,165,32,.40)',
  boxShadow: '0 18px 42px rgba(0,0,0,.46), inset 0 1px 0 rgba(255,255,255,.08)',
  backdropFilter: 'blur(18px) saturate(1.12)',
  WebkitBackdropFilter: 'blur(18px) saturate(1.12)',
},
  emojiPickerContainer: {
    // Fixed + safe-area to avoid clipping inside parents and by system UI
    position: 'fixed',
    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 66px)',
    left: '12px',
    zIndex: 1000,

    // Size tuned so it never feels full-screen nor gets cut at edges
    maxWidth: 'min(520px, 96vw)',
    maxHeight: 'min(56dvh, 520px)',
    overflowY: 'auto',
    overflowX: 'visible',

    // Visuals
    backgroundColor: 'rgba(255, 255, 255, 0.84)',
    border: '1px solid rgba(75,0,130,0.35)',
    borderRadius: '12px',
    backdropFilter: 'blur(8px) saturate(1.02)',
    WebkitBackdropFilter: 'blur(8px) saturate(1.02)',
    padding: '10px',
    boxShadow: '0 10px 26px rgba(0, 0, 0, 0.28)',
    pointerEvents: 'auto',
  },
  form: {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flex: 1,
  background: 'rgba(255,255,255,.10)',
  border: '1px solid rgba(218,165,32,.46)',
  borderRadius: 999,
  padding: 6,
  boxShadow: 'inset 0 2px 10px rgba(0,0,0,.22)',
  pointerEvents: 'auto',
},
  input: {
  flex: 1,
  minWidth: 0,
  padding: '11px 12px',
  fontSize: '0.98rem',
  borderRadius: 999,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: '#fff',
  fontWeight: 750,
},
  sendButton: {
  width: 42,
  height: 42,
  minWidth: 42,
  padding: 0,
  background: 'linear-gradient(135deg,#8A2BE2 0%, #6F1CCB 48%, #DAA520 100%)',
  color: '#fff',
  border: '1px solid rgba(255,215,0,.45)',
  borderRadius: 999,
  fontWeight: 950,
  cursor: 'pointer',
  boxShadow: '0 12px 24px rgba(0,0,0,.32), 0 0 16px rgba(218,165,32,.20)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
    fontSize: 18,
  },
  messageError: {
    margin: '7px 8px 0',
    color: '#ffd1d1',
    fontSize: 12,
    fontWeight: 700,
  },
  infoBox: {
    background: 'linear-gradient(145deg, rgba(7,0,19,.76), rgba(31,5,68,.54))',
    color: '#fff',
    padding: 20,
    borderRadius: 22,
    maxWidth: 520,
    margin: '18px auto 0 auto',
    textAlign: 'center',
    border: '1px solid rgba(218,165,32,.38)',
    boxShadow: '0 24px 54px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.08)',
    backdropFilter: 'blur(16px) saturate(1.12)',
    WebkitBackdropFilter: 'blur(16px) saturate(1.12)',
  },
  infoTitle: { fontSize: '1.2rem', fontWeight: 'bold', color: '#DAA520', marginBottom: 10 },
  infoText: { fontSize: '1rem', fontStyle: 'italic', lineHeight: 1.5 },

  /* ✅ Mover partnerCard y asociados DENTRO del objeto styles */
  partnerCard: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
    background: 'rgba(0,0,0,0.45)',
    border: '1px solid rgba(218,165,32,0.35)',
    borderRadius: 14, padding: '8px 12px', marginBottom: 12,
    boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
    position: 'relative',
    zIndex: 5,
  },
  partnerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flex: '1 1 auto',
    minWidth: 0,
    maxWidth: 'calc(100% - 178px)', // espacio para "+" (≈36px) + "Siguiente" (≥128px) + gap
    pointerEvents: 'auto'
  },
  partnerRight: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    flex: '0 0 auto',
    marginLeft: 'auto',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    position: 'relative',
    zIndex: 6,
    pointerEvents: 'auto',
  },
  partnerAvatar: {
    width: 'clamp(80px, 18vw, 120px)',
    height: 'clamp(80px, 18vw, 120px)',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid #DAA520',
    boxShadow: '0 6px 18px rgba(0,0,0,0.45), 0 0 0 4px rgba(75,0,130,0.25) inset, 0 0 0 2px rgba(255,214,101,0.25)'
  },
  partnerNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#fff',
    flexWrap: 'nowrap',
    minWidth: 0
  },
  partnerName: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 10px',
    borderRadius: 12,
    background: 'linear-gradient(135deg, rgba(0,0,0,0.55), rgba(35,0,70,0.45))',
    border: '1px solid rgba(218,165,32,0.45)',
    backdropFilter: 'blur(3px)',
    WebkitBackdropFilter: 'blur(3px)',
    fontSize: 'clamp(1rem, 3.8vw, 1.15rem)',
    fontWeight: 900,
    fontFamily: '"Cinzel", "Cormorant Garamond", serif',
    letterSpacing: '0.35px',
    color: '#FFD665',
    textShadow: '0 1px 3px rgba(0,0,0,0.6), 0 0 8px rgba(255,214,101,0.45)',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '42vw'
  },
  partnerGender: { fontSize: '1rem' },
  partnerSign: { fontSize: '1.2rem' },
  partnerHintWrap: {
    marginTop: 2,
  },
  partnerHintLine: {
    fontSize: '0.85rem',
    opacity: 0.95,
    color: '#FFFFFF',
    lineHeight: 1.2,
    textShadow: '0 1px 2px rgba(0,0,0,0.45)'
  },
  partnerHint: { fontSize: '0.85rem', opacity: 0.95, color: '#FFFFFF', textShadow: '0 1px 2px rgba(0,0,0,0.45)' },
  partnerSignText: {
    fontSize: '0.95rem', fontWeight: 600, color: '#FFD700',
    textShadow: '0 1px 3px rgba(0,0,0,0.6), 0 0 4px #4B0082', marginTop: 2,
  },
  addBtn: {
    marginLeft: 8,
    background: '#ffffff',
    color: '#4B0082',
    border: '1px solid rgba(75,0,130,0.6)',
    borderRadius: 10,
    width: 28,
    height: 28,
    lineHeight: '26px',
    textAlign: 'center',
    fontWeight: 800,
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  addBtnInline: {
    marginRight: 10,
    padding: '8px 12px',
    background: 'rgba(255,255,255,.10)',
    color: '#FFE8A3',
    border: '1px solid rgba(218,165,32,.42)',
    borderRadius: 999,
    fontWeight: 900,
    fontSize: '0.88rem',
    cursor: 'pointer',
    boxShadow: '0 8px 18px rgba(0,0,0,.22)',
    lineHeight: 1,
    minHeight: 34,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    whiteSpace: 'nowrap',
    position: 'relative',
    zIndex: 7,
    pointerEvents: 'auto',
  },
  partnerCardC: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    background: 'linear-gradient(145deg, rgba(7,0,19,.78), rgba(31,5,68,.58))',
    border: '1px solid rgba(218,165,32,.38)',
    borderRadius: 22,
    padding: '10px 12px',
    marginBottom: 12,
    boxShadow: '0 18px 40px rgba(0,0,0,.32), inset 0 1px 0 rgba(255,255,255,.08)',
    maxWidth: '420px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    backdropFilter: 'blur(16px) saturate(1.12)',
    WebkitBackdropFilter: 'blur(16px) saturate(1.12)',
  },
  partnerAvatarC: {
    width: 'clamp(80px, 22vw, 110px)',
    height: 'clamp(80px, 22vw, 110px)',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '4px solid #DAA520',
    boxShadow: '0 4px 12px rgba(0,0,0,0.35)'
  },
  partnerNameRowC: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#fff',
    flexWrap: 'nowrap',
    minWidth: 0
  },
  partnerNameC: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 6px',
    borderRadius: 12,
    background: 'linear-gradient(135deg, rgba(0,0,0,0.55), rgba(35,0,70,0.45))',
    border: '1px solid rgba(218,165,32,0.45)',
    backdropFilter: 'blur(3px)',
    WebkitBackdropFilter: 'blur(3px)',
    fontSize: 'clamp(1rem, 3.8vw, 1.15rem)',
    fontWeight: 900,
    fontFamily: '"Cinzel", "Cormorant Garamond", serif',
    letterSpacing: '0.35px',
    color: '#FFD665',
    textShadow: '0 1px 3px rgba(0,0,0,0.6), 0 0 8px rgba(255,214,101,0.45)',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '72vw'
  },
  partnerSignTextC: {
    fontSize: '0.98rem',
    fontWeight: 700,
    color: '#FFD700',
    textShadow: '0 1px 3px rgba(0,0,0,0.6), 0 0 4px #4B0082'
  },
  partnerHintC: {
    fontSize: '0.95rem',
    color: '#FFFFFF',
    textShadow: '0 1px 2px rgba(0,0,0,0.45)'
  },
  partnerActionsC: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginTop: 2,
    width: '100%',
    flexWrap: 'nowrap'
  },
  viewProfileBtn: {
    padding: '8px 12px',
    background: 'rgba(255,255,255,.10)',
    color: '#FFE8A3',
    border: '1px solid rgba(218,165,32,.42)',
    borderRadius: 999,
    fontWeight: 900,
    fontSize: '0.88rem',
    cursor: 'pointer',
    boxShadow: '0 8px 18px rgba(0,0,0,.22)',
    lineHeight: 1,
    minHeight: 34,
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    whiteSpace: 'nowrap',
  },

  /* ✅ Compact styles that were accidentally placed outside of the styles object */
  partnerInfoCompact: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flex: 1,
    gap: 6,
  },

  partnerNameRowCompact: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
    color: '#fff',
    minWidth: 0
  },

  partnerActionsCompact: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
};
